package com.yendo.mx.utils

import com.yendo.mx.models.ZonaDirectorio
import dev.gitlive.firebase.firestore.GeoPoint

/**
 * Utilidades matemáticas para validación geográfica en Yendo Mx.
 * No necesita internet para funcionar una vez que recibe los datos.
 */
object GeoUtils {

    /**
     * Algoritmo Ray-Casting:
     * Determina si un punto (lat/lng) está dentro de un polígono de GeoPoints.
     */
    fun isPointInPolygon(lat: Double, lng: Double, polygon: List<GeoPoint>): Boolean {
        var intersectCount = 0
        for (i in polygon.indices) {
            val j = (i + 1) % polygon.size

            // Verificamos si el rayo imaginario cruza los bordes del polígono
            if (((polygon[i].longitude > lng) != (polygon[j].longitude > lng)) &&
                (lat < (polygon[j].latitude - polygon[i].latitude) * (lng - polygon[i].longitude) /
                        (polygon[j].longitude - polygon[i].longitude) + polygon[i].latitude)) {
                intersectCount++
            }
        }
        // Si el número de cruces es impar, el punto está adentro
        return intersectCount % 2 != 0
    }

    /**
     * Validador Global de Cobertura:
     * Revisa todas las "islas" de servicio y las zonas de exclusión.
     */
    fun validarCoberturaTotal(lat: Double, lng: Double, zona: ZonaDirectorio): Boolean {
        // 1. Verificar si el punto cae en CUALQUIERA de los polígonos de servicio (Centro, Cobach, etc.)
        val estaEnServicio = zona.poligonos_servicio.any { wrapper ->
            isPointInPolygon(lat, lng, wrapper.coordenadas)
        }

        // 2. Verificar si el punto cae en CUALQUIERA de los polígonos de exclusión (Zonas Rojas)
        val estaEnExclusion = zona.poligonos_exclusion.any { wrapper ->
            isPointInPolygon(lat, lng, wrapper.coordenadas)
        }

        // El usuario tiene cobertura solo si está en servicio Y NO está en exclusión
        return estaEnServicio && !estaEnExclusion
    }

    /**
     * Calcula la distancia en KM entre dos puntos (Fórmula de Haversine)
     * 🚀 Útil para cotizaciones rápidas sin API externa.
     */
    fun calcularDistanciaKm(lat1: Double, lon1: Double, lat2: Double, lon2: Double): Double {
        val r = 6371.0 // Radio de la Tierra en KM
        val dLat = (lat2 - lat1) * (kotlin.math.PI / 180.0)
        val dLon = (lon2 - lon1) * (kotlin.math.PI / 180.0)
        val a = kotlin.math.sin(dLat / 2) * kotlin.math.sin(dLat / 2) +
                kotlin.math.cos(lat1 * (kotlin.math.PI / 180.0)) * kotlin.math.cos(lat2 * (kotlin.math.PI / 180.0)) *
                kotlin.math.sin(dLon / 2) * kotlin.math.sin(dLon / 2)
        val c = 2 * kotlin.math.atan2(kotlin.math.sqrt(a), kotlin.math.sqrt(1 - a))
        return r * c
    }

    /**
     * Calcula la distancia en metros entre dos puntos (Fórmula de Haversine)
     */
    fun calcularDistanciaMetros(lat1: Double, lon1: Double, lat2: Double, lon2: Double): Float {
        return (calcularDistanciaKm(lat1, lon1, lat2, lon2) * 1000.0).toFloat()
    }
}
